import * as Sentry from '@sentry/react';
import { useCallback, useContext, useRef, useState } from 'react';
import { API_CONFIG, type API } from '@/api';
import { LoadingContext } from '@/contexts/LoadingContext';
import { useAuth } from '@/hooks/useAuth';
import { fetchWithRetry } from '@/utils/retry';

type HTTPMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';
type EndpointKey<M extends HTTPMethod> = keyof API[M];
type EndpointConfig<M extends HTTPMethod, E extends EndpointKey<M>> = API[M][E];
type Parameters<M extends HTTPMethod, E extends EndpointKey<M>> =
  EndpointConfig<M, E> extends { parameters: infer P } ? P : never;
type Response<M extends HTTPMethod, E extends EndpointKey<M>> =
  EndpointConfig<M, E> extends { response: infer R } ? R : never;

type Result<M extends HTTPMethod, E extends EndpointKey<M>> =
  | { ok: true; data: Response<M, E> }
  | { ok: false; error: string; details: string[] };

interface UseApiRequestOptions {
  showGlobalLoading?: boolean;
}

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

export function useApiRequest<M extends HTTPMethod, E extends EndpointKey<M>>(
  method: M,
  endpoint: E,
  options: UseApiRequestOptions = {},
) {
  const { showGlobalLoading = true } = options;
  const [isRequesting, setIsRequesting] = useState<boolean>(false);
  const auth = useAuth();
  const loadingContext = useContext(LoadingContext);

  // useRefで関数参照を安定化（無限ループ防止）
  const loadingContextRef = useRef(loadingContext);
  loadingContextRef.current = loadingContext;

  const execute = useCallback(
    async (params: Parameters<M, E>): Promise<Result<M, E>> => {
      setIsRequesting(true);
      if (showGlobalLoading && loadingContextRef.current) {
        loadingContextRef.current.startLoading();
      }

      const validToken = await auth.ensureValidToken();

      try {
        // API設定からパスとリトライ設定を取得
        const methodConfig = API_CONFIG[method] as Record<
          string,
          { path: string; retry?: { enabled: boolean } }
        >;
        const config = methodConfig?.[endpoint as string];
        const path = config?.path || `/${String(endpoint).toLowerCase()}`;
        const retryConfig = config?.retry || { enabled: false };

        // パスパラメータを置換
        let url = `${BASE_URL}${path}`;
        const pathParams: Record<string, string | number> = {};
        const queryOrBodyParams: Record<string, unknown> = {};

        // パラメータを分類（パスパラメータとクエリ/ボディパラメータ）
        Object.entries(params as Record<string, unknown>).forEach(
          ([key, value]) => {
            if (path.includes(`:${key}`)) {
              pathParams[key] = value as string | number;
            } else {
              queryOrBodyParams[key] = value;
            }
          },
        );

        // パスパラメータを置換
        Object.entries(pathParams).forEach(([key, value]) => {
          url = url.replace(`:${key}`, String(value));
        });

        let body: string | undefined;
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${validToken}`,
        };

        if (method === 'GET') {
          const searchParams = new URLSearchParams();
          Object.entries(queryOrBodyParams).forEach(([key, value]) => {
            if (value === undefined || value === null || value === '') {
              return;
            }
            if (Array.isArray(value)) {
              // 配列の場合は key[]=value の形式で追加
              value.forEach((item) => {
                searchParams.append(`${key}[]`, String(item));
              });
            } else {
              searchParams.append(key, String(value));
            }
          });
          if (searchParams.toString()) {
            url += `?${searchParams.toString()}`;
          }
        } else if (
          method === 'DELETE' &&
          Object.keys(queryOrBodyParams).length === 0
        ) {
          // DELETEメソッドでボディが空の場合はbodyをundefinedにする
          body = undefined;
        } else {
          body = JSON.stringify(queryOrBodyParams);
        }

        // リトライロジックを使用してfetchを実行
        const response = await fetchWithRetry(
          () =>
            fetch(url, {
              method,
              headers,
              body,
            }),
          retryConfig,
        );

        if (!response.ok) {
          let errorMessage = `http.${response.status}`;
          let errorDetails: string[] = [];

          try {
            const errorData = await response.json();
            if (
              errorData &&
              typeof errorData === 'object' &&
              'message' in errorData
            ) {
              errorMessage = errorData.message as string;
              errorDetails = errorData.errors as string[];
            }
          } catch (parseError) {
            console.error('Failed to parse error response:', parseError);
            errorMessage = 'http.unknown_error';
          }

          // 500番台のサーバーエラーはSentryに送信
          if (response.status >= 500) {
            Sentry.captureException(new Error(`API Error: ${errorMessage}`), {
              tags: {
                endpoint: String(endpoint),
                method,
                statusCode: response.status,
              },
              extra: {
                url,
                errorDetails,
              },
            });
          }

          return { ok: false, error: errorMessage, details: errorDetails };
        }

        // 204 No Content の場合はレスポンスボディがない
        if (response.status === 204) {
          return { ok: true, data: {} as Response<M, E> };
        }

        const data: Response<M, E> = await response.json();
        return { ok: true, data };
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'An unknown error occurred';

        // ネットワークエラーなどの予期しないエラーをSentryに送信
        Sentry.captureException(error, {
          tags: {
            endpoint: String(endpoint),
            method,
            errorType: 'unexpected',
          },
        });

        // ネットワークエラー時は details を空にして二重表示を防ぐ
        return { ok: false, error: errorMessage, details: [] };
      } finally {
        setIsRequesting(false);
        if (showGlobalLoading && loadingContextRef.current) {
          loadingContextRef.current.stopLoading();
        }
      }
    },
    [method, endpoint, auth, showGlobalLoading],
  );

  return {
    isRequesting,
    execute,
  };
}

// APIエンドポイント定数
export const API_ENDPOINTS = {
  GET: {
    USER_ME: 'UserMe' as const,
    RESERVATION: 'Reservation' as const,
    SPACE_AVAILABILITY: 'SpaceAvailability' as const,
    RESERVATION_HISTORY: 'ReservationHistory' as const,
    RESERVATION_DETAIL: 'ReservationDetail' as const,
    SPACE_LIST: 'SpaceList' as const,
    SHOWER_TIME_SLOTS: 'ShowerTimeSlots' as const,
    SHOWER_ROOM_AVAILABLE_TIME_SLOTS: 'ShowerRoomAvailableTimeSlots' as const,
    PRIVATE_ROOM_AVAILABLE_TIME_SLOTS: 'PrivateRoomAvailableTimeSlots' as const,
    AREAS_AIRPORTS: 'AreasAirports' as const,
    AREAS_TERMINALS: 'AreasTerminals' as const,
    AREAS_LOUNGES: 'AreasLounges' as const,
    STAFF_RESERVATION_SEARCH: 'StaffReservationSearch' as const,
  },
  POST: {
    USER_REGISTRATION: 'UserRegistration' as const,
    USER_REGISTRATION_COMPLETE: 'UserRegistrationComplete' as const,
    PASSWORD_RESET_EMAIL: 'PasswordResetEmail' as const,
    USER_LOGIN: 'UserLogin' as const,
    CREATE_SHARED_DEVICE_RESERVATION: 'CreateSharedDeviceReservation' as const,
    CREATE_RESERVATION: 'CreateReservation' as const,
    // 新規認証エンドポイント
    AUTH_LOGIN: 'AuthLogin' as const,
  },
  PUT: {
    PASSWORD_RESET_COMPLETE: 'PasswordResetComplete' as const,
  },
  DELETE: {
    CANCEL_RESERVATION: 'CancelReservation' as const,
  },
} as const;

// 使用例：
// const { isRequesting, execute } = useApiRequest('GET', 'Reservation');
// const result = await execute({ reservation_no: '12345' });
// if (result.ok) {
//   console.log('予約データ:', result.data);
// } else {
//   console.error('エラー:', result.error);
// }
